import {
  apiClient,
  type ApiEnvelope,
  type ApiFailure,
  type ConsentNoticeVersionSummary,
  type PublicLookupData,
  type PublicOtpStartResponse,
  type PublicOtpVerifyResponse,
  type TagType,
} from "@/lib/api/client"
import { getOrCreateRegistrationSessionId } from "@/lib/session/public-session"

class GuardianApprovalApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = "GuardianApprovalApiError"
    this.status = status
  }
}

function getFailureMessage(body: ApiFailure | null, status: number): string {
  if (body?.error) {
    return body.error
  }

  if (body?.message) {
    return body.message
  }

  if (status >= 500) {
    return "The guardian approval request failed."
  }

  return "The request could not be completed."
}

async function request<TData>(
  path: string,
  body: Record<string, unknown>
): Promise<TData> {
  const response = await fetch(path, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Session-Id": getOrCreateRegistrationSessionId(),
    },
    body: JSON.stringify(body),
    credentials: "include",
  })

  const responseBody = (await response.json()) as ApiEnvelope<TData> | ApiFailure

  if (!response.ok) {
    throw new GuardianApprovalApiError(
      getFailureMessage(responseBody as ApiFailure, response.status),
      response.status
    )
  }

  return (responseBody as ApiEnvelope<TData>).data
}

export async function lookupGuardianApprovalTag(tag: string) {
  return apiClient.lookupPublicTag({
    intent: "registration",
    tag,
  })
}

export async function getGuardianApprovalNotice(): Promise<ConsentNoticeVersionSummary | null> {
  const response = await apiClient.getActiveConsentNotices({
    audience: "guardian",
    surface: "guardian_approval",
  })

  return response.data[0] ?? null
}

export async function startGuardianApprovalOtp(payload: {
  phoneNumber: string
  stickerId: string
  tag: string
  tagType: TagType
}) {
  return request<PublicOtpStartResponse>("/api/auth/otp/start", {
    ...payload,
    purpose: "guardian_approval",
  })
}

export async function verifyGuardianApprovalOtp(payload: {
  otp: string
  phoneNumber: string
  verificationId: string
}) {
  return request<PublicOtpVerifyResponse>("/api/auth/otp/verify", payload)
}

export async function finalizeGuardianApproval(payload: {
  noticeVersionId: string
  phoneNumber: string
  stickerId: string
  tag: string
  tagType: TagType
  verificationId: string
}) {
  return request<PublicLookupData>("/api/vehicles/guardian-approval/finalize", payload)
}
